import { arrow, autoUpdate, flip, offset, shift, size, useFloating } from "@floating-ui/react-dom";
//#region src/components/_overlay/useAnchoredPosition.ts
/** crm's `side` and `align` as a Floating UI placement. */
function toPlacement(side, align) {
	return align === "center" ? side : `${side}-${align}`;
}
/**
* Positions a floating element next to an anchor (Floating UI, fixed strategy): flips to
* the other side and shifts along it to stay in view, and follows scroll and resize.
* Sets `--ds-available-width`/`--ds-available-height` on the floating element so its
* CSS can cap its size. `side` and `align` are the resolved ones, after any flip.
*/
function useAnchoredPosition({ open, side = "bottom", align = "center", offset: offset$1 = 6, padding = 8, matchWidth = false, arrowRef, arrowPadding = 8 }) {
	const { refs, floatingStyles, placement, isPositioned, middlewareData } = useFloating({
		open,
		strategy: "fixed",
		placement: toPlacement(side, align),
		whileElementsMounted: autoUpdate,
		middleware: [
			offset(offset$1),
			flip({ padding }),
			shift({ padding }),
			size({
				padding,
				apply({ availableWidth, availableHeight, rects, elements }) {
					const style = elements.floating.style;
					style.setProperty("--ds-available-width", `${Math.max(0, availableWidth)}px`);
					style.setProperty("--ds-available-height", `${Math.max(0, availableHeight)}px`);
					if (matchWidth) style.minWidth = `${rects.reference.width}px`;
				}
			}),
			...arrowRef ? [arrow({
				element: arrowRef,
				padding: arrowPadding
			})] : []
		]
	});
	const [resolvedSide, resolvedAlign = "center"] = placement.split("-");
	return {
		/** Ref for the anchor (the trigger). */
		setAnchor: refs.setReference,
		/** Ref for the floating element. */
		setFloating: refs.setFloating,
		/** Inline styles for the floating element. */
		style: floatingStyles,
		side: resolvedSide,
		align: resolvedAlign,
		/** False until the first position is computed; hide the element until then. */
		isPositioned,
		/** With `arrowRef`: the arrow's offset along the floating element's edge, in px. */
		arrow: {
			x: middlewareData.arrow?.x,
			y: middlewareData.arrow?.y
		}
	};
}
//#endregion
export { toPlacement, useAnchoredPosition };

//# sourceMappingURL=useAnchoredPosition.js.map